import { useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useThreatActors } from "@/hooks/queries";
import {
  useRansomwareGroupsTriage,
  useMapGroupToActor,
  useCreateActorForGroup,
  type UnmappedGroupRow,
} from "@/hooks/useRansomwareGroupsTriage";
import type { ThreatActor } from "@/lib/types";

/**
 * /admin/ransomware-groups — manual triage UI.
 *
 * Mirrors the Phase C CPE vendor-triage page (`CpeMappingsPage.tsx`).
 *
 * Sections:
 *   1. Stats banner (total / mapped / unmapped / percentMapped)
 *   2. Unmapped queue (paginated, sorted by claim_count DESC)
 *      - "Map to existing" → modal with searchable threat-actor combobox
 *      - "Create new actor" → modal form with optional MITRE/MISP/Malpedia IDs
 *
 * Captain dispatched 2026-04-29 after the leak-sites auto-mapper plateaued
 * at 10.16% coverage (62/610). The remaining 548 groups need human review.
 */

// ─── Status banner (no toast — keep it simple) ───────────────────────────────

interface StatusMessage {
  kind: "success" | "error";
  text: string;
}

function StatusBanner({
  status,
  onDismiss,
}: {
  status: StatusMessage | null;
  onDismiss: () => void;
}) {
  if (!status) return null;
  const isErr = status.kind === "error";
  return (
    <div
      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
        isErr
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-emerald-200 bg-emerald-500/10 text-emerald-700"
      }`}
      role="status"
    >
      <span>{status.text}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ─── KPI tile ────────────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  warn,
}: {
  label: string;
  value: number | string;
  warn?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-bold tabular-nums ${
            warn ? "text-amber-500" : ""
          }`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Map-to-existing modal ───────────────────────────────────────────────────

interface MapModalProps {
  group: UnmappedGroupRow;
  actors: ThreatActor[] | undefined;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function MapModal({
  group,
  actors,
  onClose,
  onSuccess,
  onError,
}: MapModalProps) {
  const [search, setSearch] = useState(group.name);
  const [chosen, setChosen] = useState<ThreatActor | null>(null);
  const map = useMapGroupToActor();

  const filtered = useMemo(() => {
    if (!actors) return [];
    const q = search.trim().toLowerCase();
    if (!q) return actors.slice(0, 25);
    return actors
      .filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.aliases?.some((alias) => alias.toLowerCase().includes(q)),
      )
      .slice(0, 25);
  }, [actors, search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!chosen) return;
    try {
      const res = await map.mutateAsync({
        groupId: group.groupId,
        threatActorId: chosen.id,
      });
      onSuccess(
        `Mapped "${group.name}" → ${res.mapping.actorName} (${group.claimCount} claim${
          group.claimCount === 1 ? "" : "s"
        }).`,
      );
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to map group");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-lg bg-background border shadow-xl p-6">
        <h2 className="text-base font-semibold mb-1">
          Map ransomware group to existing actor
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Group <code className="font-mono">{group.name}</code>
          {" · "}
          {group.claimCount} claim{group.claimCount === 1 ? "" : "s"}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="actor-search">Threat actor</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="actor-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setChosen(null);
                }}
                placeholder="Search threat actors by name or alias…"
                className="pl-8"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border">
            {filtered.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                No threat actors match.
              </div>
            ) : (
              <ul role="listbox" aria-label="Threat actor results">
                {filtered.map((a) => {
                  const selected = chosen?.id === a.id;
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => setChosen(a)}
                        className={`flex w-full items-center justify-between border-b px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30 last:border-0 ${
                          selected ? "bg-muted" : ""
                        }`}
                      >
                        <div>
                          <div className="font-medium">{a.name}</div>
                          {a.aliases && a.aliases.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {a.aliases.slice(0, 4).join(", ")}
                              {a.aliases.length > 4 ? ", …" : ""}
                            </div>
                          )}
                        </div>
                        {selected && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!chosen || map.isPending}
            >
              {map.isPending ? "Saving…" : "Save mapping"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create-new-actor modal ──────────────────────────────────────────────────

interface CreateActorModalProps {
  group: UnmappedGroupRow;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function CreateActorModal({
  group,
  onClose,
  onSuccess,
  onError,
}: CreateActorModalProps) {
  const [name, setName] = useState(group.name);
  const [mitreId, setMitreId] = useState("");
  const [mispUuid, setMispUuid] = useState("");
  const [malpediaId, setMalpediaId] = useState("");
  const [reason, setReason] = useState("");
  const create = useCreateActorForGroup();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    const trimmedReason = reason.trim();
    // Both are server-required. Guarding here turns a 400 with a generic toast
    // into an inline block the operator can actually act on.
    if (!trimmed || !trimmedReason) return;
    try {
      await create.mutateAsync({
        groupId: group.groupId,
        actorName: trimmed,
        mitreId: mitreId.trim() || undefined,
        mispUuid: mispUuid.trim() || undefined,
        malpediaId: malpediaId.trim() || undefined,
        reason: trimmedReason,
      });
      onSuccess(
        `Created actor "${trimmed}" + mapped "${group.name}" (${group.claimCount} claim${
          group.claimCount === 1 ? "" : "s"
        }).`,
      );
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to create actor");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-lg bg-background border shadow-xl p-6">
        <h2 className="text-base font-semibold mb-1">Create new threat actor</h2>
        <p className="text-xs text-muted-foreground mb-4">
          For group <code className="font-mono">{group.name}</code>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-actor-name">Name *</Label>
            <Input
              id="new-actor-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-actor-mitre">
              MITRE ID{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="new-actor-mitre"
              placeholder="G0046"
              value={mitreId}
              onChange={(e) => setMitreId(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-actor-misp">
              MISP UUID{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="new-actor-misp"
              placeholder="00000000-0000-0000-0000-000000000000"
              value={mispUuid}
              onChange={(e) => setMispUuid(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-actor-malpedia">
              Malpedia ID{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="new-actor-malpedia"
              placeholder="win.lockbit"
              value={malpediaId}
              onChange={(e) => setMalpediaId(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-actor-reason">Reason *</Label>
            <Input
              id="new-actor-reason"
              required
              maxLength={500}
              placeholder="Why this is a distinct canonical actor"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Recorded as the provenance for this human-asserted identity.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || !reason.trim() || create.isPending}
            >
              {create.isPending ? "Creating…" : "Create + map"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Unmapped queue panel ────────────────────────────────────────────────────

const PAGE_SIZE = 50;

interface UnmappedPanelProps {
  rows: UnmappedGroupRow[];
  page: number;
  hasMore: boolean;
  onPrev: () => void;
  onNext: () => void;
  onMapExisting: (row: UnmappedGroupRow) => void;
  onCreateNew: (row: UnmappedGroupRow) => void;
  isLoading: boolean;
}

function UnmappedPanel({
  rows,
  page,
  hasMore,
  onPrev,
  onNext,
  onMapExisting,
  onCreateNew,
  isLoading,
}: UnmappedPanelProps) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" />
          Unmapped ransomware groups
        </CardTitle>
        <div className="flex items-center gap-1 text-sm">
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0"
            onClick={onPrev}
            disabled={page <= 1 || isLoading}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-muted-foreground tabular-nums px-2">
            Page {page}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0"
            onClick={onNext}
            disabled={!hasMore || isLoading}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No unmapped ransomware groups.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                  <th className="px-4 py-2 font-medium">Group</th>
                  <th className="px-4 py-2 font-medium tabular-nums">
                    Aliases
                  </th>
                  <th className="px-4 py-2 font-medium tabular-nums">
                    Claims
                  </th>
                  <th className="px-4 py-2 font-medium">Last seen</th>
                  <th className="px-4 py-2 font-medium">Sample claim</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.groupId}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-2">
                      <div className="font-mono text-xs">{r.name}</div>
                      {r.aliases.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {r.aliases.slice(0, 3).join(", ")}
                          {r.aliases.length > 3 ? ", …" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      {r.aliasCount}
                    </td>
                    <td className="px-4 py-2 tabular-nums font-medium">
                      {r.claimCount}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                      {r.lastSeenAt
                        ? new Date(r.lastSeenAt).toISOString().slice(0, 10)
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.sampleClaimUrl ? (
                        <a
                          href={r.sampleClaimUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                          title={r.sampleClaimUrl}
                        >
                          <Badge variant="outline" className="text-xs">
                            {r.sampleVictimName ?? "view"}
                          </Badge>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => onMapExisting(r)}
                        >
                          Map to existing
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          className="h-7 text-xs"
                          onClick={() => onCreateNew(r)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Create new
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RansomwareGroupsTriagePage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  const {
    data,
    isLoading: groupsLoading,
    isError,
    error,
  } = useRansomwareGroupsTriage(page, PAGE_SIZE);
  const { data: actors } = useThreatActors();

  // Modal state
  const [mapModal, setMapModal] = useState<UnmappedGroupRow | null>(null);
  const [createModal, setCreateModal] = useState<UnmappedGroupRow | null>(null);

  function flashSuccess(text: string) {
    setStatus({ kind: "success", text });
  }
  function flashError(text: string) {
    setStatus({ kind: "error", text });
  }

  const stats = data?.stats;
  const unmapped = data?.unmapped;
  const hasMore =
    !!unmapped && unmapped.rows.length === unmapped.pageSize;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Ransomware groups — manual triage
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Map ransomware leak-site groups to threat actors. Auto-mapper
          plateaued at low coverage; remaining groups need human review.
        </p>
      </div>

      <StatusBanner status={status} onDismiss={() => setStatus(null)} />

      {/* Stats banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiTile
          label="Total groups"
          value={groupsLoading ? "—" : (stats?.totalGroups ?? 0)}
        />
        <KpiTile
          label="Mapped"
          value={groupsLoading ? "—" : (stats?.mappedGroups ?? 0)}
        />
        <KpiTile
          label="Unmapped"
          value={groupsLoading ? "—" : (stats?.unmappedGroups ?? 0)}
          warn={(stats?.unmappedGroups ?? 0) > 0}
        />
        <KpiTile
          label="% mapped"
          value={
            groupsLoading
              ? "—"
              : `${(stats?.percentMapped ?? 0).toFixed(2)}%`
          }
          warn={(stats?.percentMapped ?? 0) < 50}
        />
      </div>

      {isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Failed to load groups:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </div>
      )}

      <UnmappedPanel
        rows={unmapped?.rows ?? []}
        page={page}
        hasMore={hasMore}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
        onMapExisting={(r) => setMapModal(r)}
        onCreateNew={(r) => setCreateModal(r)}
        isLoading={groupsLoading}
      />

      {mapModal && (
        <MapModal
          group={mapModal}
          actors={actors}
          onClose={() => setMapModal(null)}
          onSuccess={flashSuccess}
          onError={flashError}
        />
      )}
      {createModal && (
        <CreateActorModal
          group={createModal}
          onClose={() => setCreateModal(null)}
          onSuccess={flashSuccess}
          onError={flashError}
        />
      )}
    </div>
  );
}

export default RansomwareGroupsTriagePage;
